import { useState, useMemo } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Copy, Check, Code, Eye } from 'lucide-react';

const VERTICALS = [
  { value: 'auto_insurance', label: 'Auto Insurance' },
  { value: 'home_insurance', label: 'Home Insurance' },
  { value: 'debt_relief', label: 'Debt Relief' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'solar', label: 'Solar' },
  { value: 'sweepstakes', label: 'Sweepstakes' },
];

const POSITIONS = [
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
];

export default function PortalEmbed() {
  const [vertical, setVertical] = useState('auto_insurance');
  const [source, setSource] = useState('');
  const [position, setPosition] = useState('bottom-right');
  const [color, setColor] = useState('#10b981');
  const [text, setText] = useState('Get Your Quote');
  const [autoOpen, setAutoOpen] = useState('');
  const [brandName, setBrandName] = useState('');
  const [brandColor, setBrandColor] = useState('');
  const [brandLogo, setBrandLogo] = useState('');
  const [brandTagline, setBrandTagline] = useState('');
  const [copied, setCopied] = useState(false);

  const snippet = useMemo(() => {
    const attrs: string[] = [
      `src="https://intentdrive.ai/embed.js"`,
      `data-vertical="${vertical}"`,
    ];
    if (source) attrs.push(`data-source="${source}"`);
    if (position !== 'bottom-right') attrs.push(`data-position="${position}"`);
    if (color !== '#10b981') attrs.push(`data-color="${color}"`);
    if (text !== 'Get Your Quote') attrs.push(`data-text="${text}"`);
    if (autoOpen) attrs.push(`data-auto-open="${autoOpen}"`);
    if (brandName) attrs.push(`data-brand-name="${brandName}"`);
    if (brandColor) attrs.push(`data-brand-color="${brandColor}"`);
    if (brandLogo) attrs.push(`data-brand-logo="${brandLogo}"`);
    if (brandTagline) attrs.push(`data-brand-tagline="${brandTagline}"`);

    return `<script\n  ${attrs.join('\n  ')}>\n</script>`;
  }, [vertical, source, position, color, text, autoOpen, brandName, brandColor, brandLogo, brandTagline]);

  function handleCopy() {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // Build a preview iframe src from the current settings
  const previewSrc = useMemo(() => {
    const params = new URLSearchParams({
      embedded: 'true',
      vertical,
    });
    if (source) params.set('source', source);
    if (brandName) params.set('brand_name', brandName);
    if (brandColor) params.set('brand_color', brandColor);
    if (brandLogo) params.set('brand_logo', brandLogo);
    if (brandTagline) params.set('brand_tagline', brandTagline);
    return `/try?${params.toString()}`;
  }, [vertical, source, brandName, brandColor, brandLogo, brandTagline]);

  return (
    <PortalLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Code className="h-6 w-6 text-emerald-400" />
            Embed for Affiliates
          </h1>
          <p className="text-slate-400 mt-1">
            Generate an embed snippet that affiliates can drop onto any landing page.
            It adds a floating button that opens the IntentDrive scoring widget.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* ── Left: Configuration ─────────────────────────────────── */}
          <div className="space-y-6">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 space-y-5">
              <h2 className="text-lg font-semibold text-white">Configuration</h2>

              {/* Vertical */}
              <div className="space-y-2">
                <Label className="text-slate-300">Vertical</Label>
                <Select value={vertical} onValueChange={setVertical}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {VERTICALS.map((v) => (
                      <SelectItem key={v.value} value={v.value} className="text-white hover:bg-slate-700">
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Source */}
              <div className="space-y-2">
                <Label className="text-slate-300">Traffic Source / Affiliate ID</Label>
                <Input
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  placeholder="e.g. my_affiliate_id"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>

              {/* Position */}
              <div className="space-y-2">
                <Label className="text-slate-300">Button Position</Label>
                <Select value={position} onValueChange={setPosition}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {POSITIONS.map((p) => (
                      <SelectItem key={p.value} value={p.value} className="text-white hover:bg-slate-700">
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Color */}
              <div className="space-y-2">
                <Label className="text-slate-300">Button Color</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="h-10 w-14 rounded border border-slate-700 bg-slate-800 cursor-pointer p-1"
                  />
                  <Input
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white font-mono max-w-[120px]"
                  />
                </div>
              </div>

              {/* Button Text */}
              <div className="space-y-2">
                <Label className="text-slate-300">Button Text</Label>
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>

              {/* Auto Open */}
              <div className="space-y-2">
                <Label className="text-slate-300">Auto-Open Delay (seconds)</Label>
                <Input
                  type="number"
                  min="0"
                  value={autoOpen}
                  onChange={(e) => setAutoOpen(e.target.value)}
                  placeholder="Leave empty to disable"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
                <p className="text-xs text-slate-500">
                  Automatically open the widget after this many seconds. Leave empty to require a click.
                </p>
              </div>
            </div>

            {/* ── Branding ─────────────────────────────────────────── */}
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 space-y-5">
              <h2 className="text-lg font-semibold text-white">Custom Branding</h2>
              <p className="text-sm text-slate-400">
                Override the default IntentDrive branding with your own brand identity.
              </p>

              {/* Brand Name */}
              <div className="space-y-2">
                <Label className="text-slate-300">Brand Name</Label>
                <Input
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="IntentDrive"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>

              {/* Brand Color */}
              <div className="space-y-2">
                <Label className="text-slate-300">Brand Color</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={brandColor || '#10b981'}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="h-10 w-14 rounded border border-slate-700 bg-slate-800 cursor-pointer p-1"
                  />
                  <Input
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    placeholder="#10b981"
                    className="bg-slate-800 border-slate-700 text-white font-mono max-w-[120px] placeholder:text-slate-500"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Applied to header, buttons, and progress indicators in the widget.
                </p>
              </div>

              {/* Brand Logo */}
              <div className="space-y-2">
                <Label className="text-slate-300">Brand Logo URL</Label>
                <Input
                  value={brandLogo}
                  onChange={(e) => setBrandLogo(e.target.value)}
                  placeholder="https://example.com/logo.png"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
                <p className="text-xs text-slate-500">
                  Replaces the default bot icon in the widget header. Use a square image.
                </p>
              </div>

              {/* Brand Tagline */}
              <div className="space-y-2">
                <Label className="text-slate-300">Brand Tagline</Label>
                <Input
                  value={brandTagline}
                  onChange={(e) => setBrandTagline(e.target.value)}
                  placeholder="e.g. Your trusted insurance advisor"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
                <p className="text-xs text-slate-500">
                  Shown under the brand name in the widget header.
                </p>
              </div>
            </div>

            {/* ── Snippet Output ─────────────────────────────────────── */}
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Embed Code</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-1 text-emerald-400" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <pre className="bg-slate-950 rounded-lg p-4 text-sm text-emerald-300 font-mono overflow-x-auto whitespace-pre-wrap break-all border border-slate-800">
                {snippet}
              </pre>
              <p className="text-xs text-slate-500">
                Paste this snippet just before the closing <code className="text-slate-400">&lt;/body&gt;</code> tag on any landing page.
              </p>
            </div>
          </div>

          {/* ── Right: Live Preview ─────────────────────────────────── */}
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 space-y-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Eye className="h-5 w-5 text-emerald-400" />
                Widget Preview
              </h2>
              <p className="text-sm text-slate-400">
                This shows how the widget looks once opened. On the affiliate's site
                it appears as a floating button that expands into this popup.
              </p>
              <div className="rounded-lg border border-slate-700 overflow-hidden bg-slate-950" style={{ height: 520 }}>
                <iframe
                  src={previewSrc}
                  className="w-full h-full border-none"
                  title="Widget Preview"
                />
              </div>
            </div>

            {/* Button preview */}
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 space-y-4">
              <h2 className="text-sm font-semibold text-slate-300">Button Preview</h2>
              <div className="flex items-center justify-center py-4">
                <span
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-white text-sm font-semibold shadow-lg"
                  style={{ background: color }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
                  </svg>
                  {text}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
